import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import founderApi, { type ApiResponse } from "../services/founderApi";
import { registerAuthSessionInvalidationHandler } from "../services/authSessionInvalidation";

export type FounderAuthUser = {
  id: string;
  email: string;
  fullName: string;
  role: "founder";
};

export type FounderSecuritySummary = {
  accountStatus: string;
  loginProtectionLabel: string;
  loginProtectionStatus: "protected" | "attention";
  lastSuccessfulLogin: string | null;
  recentFailedAttempts: number;
  activeSessions: number;
  twoStepVerification: string;
};

type FounderAuthContextValue = {
  user: FounderAuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<
    | { completed: true; user: FounderAuthUser }
    | { completed: false; requiresMfa: true; challengeId: string }
  >;
  completeMfaLogin: (challengeId: string, verificationCode: string) => Promise<FounderAuthUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  fetchSecuritySummary: () => Promise<FounderSecuritySummary>;
  signOutOtherSessions: () => Promise<void>;
};

const FounderAuthContext = createContext<FounderAuthContextValue | undefined>(undefined);

export function FounderAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FounderAuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const response = await founderApi.get<ApiResponse<{ user: FounderAuthUser }>>(
        "/auth/founder/me",
      );
      setUser(response.data.data.user);
    } catch {
      setUser(null);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await founderApi.post("/auth/founder/logout");
    } catch {
      // Clear local state even if network fails.
    } finally {
      setUser(null);
    }
  }, []);

  useEffect(() => registerAuthSessionInvalidationHandler(() => setUser(null)), []);

  useEffect(() => {
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await founderApi.post<
      ApiResponse<
        | { user: FounderAuthUser }
        | { requiresMfa: true; challengeId: string }
      >
    >("/auth/founder/login", { email, password });

    const data = response.data.data;
    if ("requiresMfa" in data && data.requiresMfa) {
      return {
        completed: false as const,
        requiresMfa: true as const,
        challengeId: data.challengeId,
      };
    }

    const nextUser = (data as { user: FounderAuthUser }).user;
    setUser(nextUser);
    return { completed: true as const, user: nextUser };
  }, []);

  const completeMfaLogin = useCallback(async (challengeId: string, verificationCode: string) => {
    const response = await founderApi.post<ApiResponse<{ user: FounderAuthUser }>>(
      "/auth/founder/login/mfa",
      { challengeId, verificationCode },
    );
    const nextUser = response.data.data.user;
    setUser(nextUser);
    return nextUser;
  }, []);

  const fetchSecuritySummary = useCallback(async () => {
    const response = await founderApi.get<ApiResponse<FounderSecuritySummary>>(
      "/auth/founder/security-summary",
    );
    return response.data.data;
  }, []);

  const signOutOtherSessions = useCallback(async () => {
    await founderApi.post("/auth/founder/sessions/revoke-others");
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      login,
      completeMfaLogin,
      logout,
      refreshUser,
      fetchSecuritySummary,
      signOutOtherSessions,
    }),
    [
      user,
      isLoading,
      login,
      completeMfaLogin,
      logout,
      refreshUser,
      fetchSecuritySummary,
      signOutOtherSessions,
    ],
  );

  return (
    <FounderAuthContext.Provider value={value}>{children}</FounderAuthContext.Provider>
  );
}

export function useFounderAuth(): FounderAuthContextValue {
  const context = useContext(FounderAuthContext);
  if (!context) {
    throw new Error("useFounderAuth must be used within FounderAuthProvider.");
  }
  return context;
}
